import { PlusIcon } from '@commenda/icons';
import { cn } from '@commenda/utils';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../internal/Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { ScrollArea } from '../ScrollArea';

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  actionHref?: string;
  actionLabel?: string;
  hidden?: boolean;
  // Extra text matched by search but not shown (e.g. a price's code + suite behind its name).
  keywords?: string;
  /**
   * Optional group label. When any option has a `group`, options render under group headings (ordered
   * by first appearance). Options without a `group` fall into an unlabeled section. Search still
   * filters across every group.
   */
  group?: string;
}

export interface SingleComboboxProps {
  _className?: string;
  options: Option[];
  renderOption?: (props: {
    option: Option;
    isSelected: boolean;
    onSelect: (value: string) => void;
  }) => React.ReactNode;
  renderTrigger?: (props: {
    selectedOption: Option | undefined;
    placeholder: string;
  }) => React.ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  onSearchChange?: (query: string) => void;
  addItemConfig?: {
    shouldAllowAdd: boolean;
    onAddItem: (searchText: string) => void;
    placeholder: string;
    // When set, the add row reflects the current search (`Add "<text>"`) so a typed value can be
    // created inline; falls back to `placeholder` when the search is empty.
    createFromSearch?: boolean;
  };
  notFoundText?: string;
  value: string;
  setValue: (val: string) => void;
  error?: string;
  disabled?: boolean;
  id?: string;
  hideSearch?: boolean;
  canClearSelection?: boolean;
  _optionsClassName?: string;
  _listClassName?: string;
  selectedItemClassName?: string;
  useRenderTriggerForPlaceholder?: boolean;
  readonly?: boolean;
  useScrollArea?: boolean;
  modalPopover?: boolean;
}

// Sentinel group key for options that carry no `group` — kept unlabeled in the list.
const UNGROUPED_KEY = '__ungrouped__';

const SingleCombobox: React.FC<SingleComboboxProps> = ({
  _className,
  options,
  renderOption,
  addItemConfig,
  renderTrigger,
  placeholder = 'Select option',
  searchPlaceholder = 'Search option...',
  onSearchChange,
  notFoundText = 'No results.',
  setValue,
  value,
  error = false,
  disabled = false,
  id,
  hideSearch = false,
  _optionsClassName,
  _listClassName,
  selectedItemClassName,
  canClearSelection = true,
  useRenderTriggerForPlaceholder = false,
  readonly = false,
  useScrollArea = false,
  modalPopover = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  // cmdk highlights an item by mouse-enter and only clears the
  // `data-selected` attribute when another item is highlighted or the menu
  // closes — so a direct mouse-out leaves a sticky highlight. We control
  // cmdk's value here and reset it on the list's mouseleave so the bg
  // clears cleanly. Keyboard navigation isn't affected (mouseleave doesn't
  // fire while the cursor is stationary).
  const [activeValue, setActiveValue] = React.useState('');

  const handleSelect = (currentValue: string) => {
    setValue(canClearSelection && currentValue === value ? '' : currentValue);
    setOpen(false);
  };

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = options.filter((option) =>
    `${option?.label ?? ''} ${option?.keywords ?? ''}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const visibleOptions = filteredOptions.filter((option) => !option.hidden);
  const isGrouped = visibleOptions.some((option) => option.group != null);
  // Group labels in first-appearance order, so the caller controls section ordering via option order.
  const groupOrder = visibleOptions.reduce<string[]>((order, option) => {
    const key = option.group ?? UNGROUPED_KEY;
    if (!order.includes(key)) order.push(key);

    return order;
  }, []);

  const renderOptionItem = (option: Option) => {
    const key = `${option.value}-${option.label}`;
    const item = (
      <CommandItem
        key={key}
        value={option.value}
        disabled={option.disabled}
        onSelect={handleSelect}
        className={cn(
          option.actionHref && 'min-w-0 flex-1',
          value === option.value ? selectedItemClassName : undefined
        )}
      >
        {renderOption ? (
          renderOption({
            option,
            isSelected: value === option.value,
            onSelect: handleSelect,
          })
        ) : (
          <>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{option.label}</span>
              {option.description ? (
                <span className="txt-extra-small-regular text-text-tertiary">
                  {option.description}
                </span>
              ) : null}
            </span>
            <Check
              className={cn(
                'ml-auto h-4 w-4',
                value === option.value ? 'opacity-100' : 'opacity-0'
              )}
            />
          </>
        )}
      </CommandItem>
    );

    if (!option.actionHref || !option.actionLabel) return item;

    return (
      <div key={key} className="flex items-center gap-xs px-lg">
        {item}
        <a
          href={option.actionHref}
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 txt-extra-small-medium text-fg-brand underline underline-offset-2"
        >
          {option.actionLabel}
        </a>
      </div>
    );
  };

  const onPopoverOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
      onSearchChange?.('');
      setActiveValue('');
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={onPopoverOpenChange}
      modal={modalPopover}
    >
      <PopoverTrigger asChild aria-readonly={readonly}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            // Base sizing and typography to match SelectTrigger/Input
            'flex h-10 w-full box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary text-text-primary txt-small-regular ring-0 transition-colors [&>span]:line-clamp-1',
            // Disabled state and baseline focus ring
            'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
            // Border defaults and hover/focus colors (non-error)
            'border border-border-primary hover:border-border-primary-hover disabled:border-border-disabled',
            // Open state ring logic; hide border to avoid double outline
            'aria-expanded:ring-4 aria-expanded:ring-[var(--color-bg-brand-hover)] aria-expanded:border-fg-brand',
            // Error variants
            error
              ? 'border-border-error hover:border-border-error focus-visible:border-border-error-hover focus-visible:ring-border-error-hover'
              : '',
            _className
          )}
        >
          {renderTrigger &&
          (useRenderTriggerForPlaceholder || selectedOption) ? (
            renderTrigger({
              selectedOption,
              placeholder,
            })
          ) : (
            <span
              className={cn(
                'flex-grow text-left',
                !selectedOption && 'text-text-placeholder txt-small-regular'
              )}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 ml-2 text-fg-quaternary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', _optionsClassName)}
        align="start"
        style={{
          width: 'var(--radix-popper-anchor-width)',
        }}
      >
        <Command
          shouldFilter={false}
          value={activeValue}
          onValueChange={setActiveValue}
        >
          {!hideSearch ? (
            <CommandInput
              data-testid="combobox-search"
              placeholder={searchPlaceholder || 'Search option...'}
              value={searchQuery}
              onValueChange={(query) => {
                setSearchQuery(query);
                onSearchChange?.(query);
              }}
            />
          ) : null}
          <CommandList
            className={cn(
              useScrollArea ? 'overflow-hidden' : 'max-h-60 overflow-y-auto',
              _listClassName
            )}
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onMouseLeave={() => setActiveValue('')}
          >
            {(() => {
              const listContent = (
                <>
                  {addItemConfig?.shouldAllowAdd ? (
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        addItemConfig.onAddItem(searchQuery.trim());
                        setSearchQuery('');
                      }}
                      className="mx-0 rounded-none justify-start"
                    >
                      <PlusIcon className="mr-2 size-4" />
                      {addItemConfig.createFromSearch && searchQuery.trim()
                        ? `Add "${searchQuery.trim()}"`
                        : addItemConfig.placeholder}
                    </CommandItem>
                  ) : null}
                  {addItemConfig?.shouldAllowAdd ? <CommandSeparator /> : null}

                  <CommandEmpty>{notFoundText}</CommandEmpty>
                  {isGrouped ? (
                    groupOrder.map((groupKey) => {
                      const groupOptions = visibleOptions.filter(
                        (option) => (option.group ?? UNGROUPED_KEY) === groupKey
                      );
                      if (!groupOptions.length) return null;

                      return (
                        <CommandGroup
                          key={groupKey}
                          className="[&_[cmdk-group-heading]]:!px-lg [&_[cmdk-group-heading]]:!py-sm [&_[cmdk-group-heading]]:!text-xs [&_[cmdk-group-heading]]:!font-medium [&_[cmdk-group-heading]]:!text-text-quaternary"
                          {...(groupKey !== UNGROUPED_KEY
                            ? { heading: groupKey }
                            : {})}
                        >
                          {groupOptions.map(renderOptionItem)}
                        </CommandGroup>
                      );
                    })
                  ) : (
                    <CommandGroup>
                      {visibleOptions.map(renderOptionItem)}
                    </CommandGroup>
                  )}
                </>
              );

              return useScrollArea ? (
                <ScrollArea viewportClassName="max-h-60">
                  {listContent}
                </ScrollArea>
              ) : (
                listContent
              );
            })()}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { SingleCombobox };
